import { lazy, Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { AppLayout } from "../components/layout/AppLayout/AppLayout";
import { FullPageLoadingSpinner } from "../components/elements/FullPageLoadingSpinner";
import { selectUserIsLoggedIn } from "../store/reducers/auth/auth.slice";
import Plan from "../pages/App/Plan";
import Challenge from "../pages/App/components/Challenge";

// No auth pages
const LoginPage = lazy(() => import("../pages/Auth/LoginPage/LoginPage"));
const JoinUsPage = lazy(() => import("../pages/Auth/JoinUsPage/JoinUsPage"));
const RegisterPage = lazy(
  () => import("../pages/Auth/RegisterPage/RegisterPage")
);
const ForgotPasswordPage = lazy(
  () => import("../pages/Auth/ForgotPasswordPage/ForgotPasswordPage")
);
const SetNewPasswordPage = lazy(
  () => import("../pages/Auth/ForgotPasswordPage/SetNewPasswordPage")
);
const NotFoundPage = lazy(
  () => import("../pages/Shared/NotFoundPage/NotFoundPage")
);

// App pages
const EventsPage = lazy(
  () => import("../pages/App/components/EventPage/EventsPage")
);
const MusicPage = lazy(() => import("../pages/App/MusicPage"));
const Faqs = lazy(() => import("../pages/App/components/FaqPage/Faqs"));
const ContactUs = lazy(
  () => import("../pages/App/components/Contactus/Contactus")
);
const ProfilePage = lazy(() => import("../pages/App/ProfilePage"));
const ShopPage = lazy(
  () => import("../pages/App/components/ShopPage/ShopPage")
);
const ShopDetails = lazy(
  () => import("../pages/App/components/ShopPage/ShopDetails")
);
const ClassesPage = lazy(
  () => import("../pages/App/components/ClassPage/ClassesPage")
);
const ClassDetails = lazy(
  () => import("../pages/App/components/ClassPage/ClassDetails")
);
const EventDetails = lazy(
  () => import("../pages/App/components/EventPage/EventDetails")
);
const PrivacyPage = lazy(() => import("../pages/App/PrivacyPage"));
const HomePage = lazy(() => import("../pages/App/HomePage"));
const BlogPage = lazy(() => import("../pages/App/BlogPage"));

// Payment Pages
const PaymentSuccessPage = lazy(
  () => import("../pages/Shared/PaymentSuccessPage")
);
const PaymentFailurePage = lazy(
  () => import("../pages/Shared/PaymentFailurePage")
);

// --- Start of Fix ---

// Use React.ReactElement instead of React.ReactNode
interface PrivateRouteProps {
  children: React.ReactElement;
}

const PrivateRoute = ({ children }: PrivateRouteProps) => {
  const token = sessionStorage.getItem("STORAGE_TOKEN_KEY_standin");

  return token ? children : <Navigate to="/login" />;
};

// Use React.ReactElement instead of React.ReactNode
interface PublicRouteProps {
  children: React.ReactElement;
}

const PublicRoute = ({ children }: PublicRouteProps) => {
  const token = sessionStorage.getItem("STORAGE_TOKEN_KEY_standin");

  return !token ? children : <Navigate to="/" />;
};

// --- End of Fix ---


export const AppRouter = () => {
  const currentUser = useSelector(selectUserIsLoggedIn);
  const token = sessionStorage.getItem("STORAGE_TOKEN_KEY_standin");

  return (
    <Suspense fallback={<FullPageLoadingSpinner isLoading />}>
      <AppLayout authorized={currentUser}>
        <Routes>
          <Route path="blog" element={<BlogPage />} />
          <Route path="about" element={<MusicPage />} />
          <Route path="faqs" element={<Faqs />} />
          <Route path="plan" element={<Plan />} />
          <Route path="contact-us" element={<ContactUs name="Name" />} />

          {/* Classes Routes */}
          <Route
            path="classes"
            element={
              <PrivateRoute>
                <ClassesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="classes/classDetails/:id"
            element={
              <PrivateRoute>
                <ClassDetails />
              </PrivateRoute>
            }
          />

          <Route
            path="profile"
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            }
          />
          <Route path="privacy" element={<PrivacyPage />} />
          
          {/* Payment Routes */}
          <Route path="payments">
            <Route
              path="success"
              element={
                <PrivateRoute>
                  <PaymentSuccessPage />
                </PrivateRoute>
              }
            />
            <Route
              path="cancel"
              element={
                <PrivateRoute>
                  <PaymentFailurePage />
                </PrivateRoute>
              }
            />
          </Route>

          <Route path="/" element={<HomePage />} />
          
          {/* Auth Routes */}
          {!token && <Route path="login" element={<LoginPage />} />}
          {!token && <Route path="register" element={<RegisterPage />} />}
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          {!token && (
            <Route
              path="set-new-password/:userId/:hash"
              element={<SetNewPasswordPage />}
            />
          )}

          {/* Events Routes */}
          <Route
            path="events"
            element={
              <PrivateRoute>
                <EventsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="events/eventDetails/:id"
            element={
              <PrivateRoute>
                <EventDetails />
              </PrivateRoute>
            }
          />

          {/* Shop Routes */}
          <Route
            path="shop"
            element={
              <PrivateRoute>
                <ShopPage />
              </PrivateRoute>
            }
          />
          <Route path="shops/shopDetails/:id" element={<ShopDetails />} />
          <Route path="challenge" element={<Challenge />} />
          
          <Route path="join-us" element={<PublicRoute><JoinUsPage /></PublicRoute>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AppLayout>
    </Suspense>
  );
};
